import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Save, Upload, Image as ImageIcon, X, RefreshCw, Link as LinkIcon, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeader } from "@/context/AuthContext";
import AdminNav from "@/components/AdminNav";
import { resizeImage, FALLBACK } from "@/lib/gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "tentang", label: "Tentang Pondok" },
  { key: "fasilitas", label: "Fasilitas" },
  { key: "pengajar", label: "Pengajar" },
  { key: "galeri", label: "Galeri" },
  { key: "pendaftaran", label: "Pendaftaran" },
  { key: "iklan", label: "Iklan Berjalan" },
  { key: "statistik", label: "Statistik" },
];

const emptyGallery = {
  tentang: { image1: "", image2: "", description: "" },
  fasilitas: Array.from({ length: 5 }, () => ({ name: "", desc: "", image: "" })),
  pengajar: Array.from({ length: 4 }, () => ({ jabatan: "", nama: "", image: "" })),
  galeri: Array.from({ length: 8 }, () => ({ caption: "", image: "" })),
  pendaftaran: { image: "" },
  iklan: Array.from({ length: 5 }, () => ({ image: "", link: "" })),
};

const emptyStats = {
  total_santri_aktif: 100,
  total_hafidz_alumni: 400,
  total_pengajar: 15,
  tahun_berdiri: 2013,
};

export default function AdminGaleri() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("tentang");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(emptyGallery);
  const [stats, setStats] = useState(emptyStats);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [gRes, sRes] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/stats`),
      ]);
      const g = gRes.data || {};
      setData({
        tentang: g.tentang || emptyGallery.tentang,
        fasilitas: (g.fasilitas || []).length === 5 ? g.fasilitas : emptyGallery.fasilitas,
        pengajar: (g.pengajar || []).length === 4 ? g.pengajar : emptyGallery.pengajar,
        galeri: (g.galeri || []).length === 8 ? g.galeri : emptyGallery.galeri,
        pendaftaran: g.pendaftaran || emptyGallery.pendaftaran,
        iklan: (g.iklan || []).length === 5 ? g.iklan : emptyGallery.iklan,
      });
      setStats({ ...emptyStats, ...(sRes.data || {}) });
    } catch (e) {
      if (e?.response?.status === 401) {
        logout(); navigate("/login", { replace: true });
      } else toast.error("Gagal memuat data galeri");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveSection = async (section) => {
    setSaving(true);
    try {
      if (section === "tentang") {
        await axios.put(`${API}/gallery/tentang`, data.tentang, { headers: authHeader() });
      } else if (section === "pendaftaran") {
        await axios.put(`${API}/gallery/pendaftaran`, data.pendaftaran, { headers: authHeader() });
      } else if (section === "statistik") {
        await axios.put(`${API}/stats`, stats, { headers: authHeader() });
      } else {
        await axios.put(`${API}/gallery/${section}`, { items: data[section] }, { headers: authHeader() });
      }
      toast.success("Perubahan disimpan.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menyimpan";
      toast.error(String(msg));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-parchment text-forest">
      <AdminNav user={user} active="galeri" />
      <main className="max-w-[1400px] mx-auto pt-28 pb-16 px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase text-gold">Konten Landing Page</p>
            <h1 className="mt-1 font-serif-display text-forest text-3xl md:text-4xl">Kelola Galeri</h1>
            <p className="mt-1 text-sm text-moss max-w-2xl">
              Perbarui gambar & teks untuk 5 seksi landing page. Foto lama akan otomatis ditimpa saat Anda mengganti file.
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-forest text-xs hover:bg-gold/20"
            data-testid="galeri-reload"
          >
            <RefreshCw size={14} /> Muat ulang
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-full text-xs tracking-wide transition-colors ${
                tab === t.key ? "bg-forest text-parchment" : "bg-sand text-forest hover:bg-gold/20"
              }`}
              data-testid={`galeri-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24 text-moss text-sm">
            <Loader2 className="animate-spin mr-2" size={16} /> Memuat data…
          </div>
        ) : (
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-white rounded-2xl border border-gold/30 p-6 md:p-8"
          >
            {tab === "tentang" && (
              <TentangEditor data={data.tentang} onChange={(v) => setData({ ...data, tentang: v })} />
            )}
            {tab === "fasilitas" && (
              <FasilitasEditor items={data.fasilitas} onChange={(v) => setData({ ...data, fasilitas: v })} />
            )}
            {tab === "pengajar" && (
              <PengajarEditor items={data.pengajar} onChange={(v) => setData({ ...data, pengajar: v })} />
            )}
            {tab === "galeri" && (
              <GaleriEditor items={data.galeri} onChange={(v) => setData({ ...data, galeri: v })} />
            )}
            {tab === "pendaftaran" && (
              <PendaftaranEditor data={data.pendaftaran} onChange={(v) => setData({ ...data, pendaftaran: v })} />
            )}
            {tab === "iklan" && (
              <IklanEditor items={data.iklan} onChange={(v) => setData({ ...data, iklan: v })} />
            )}
            {tab === "statistik" && (
              <StatistikEditor stats={stats} onChange={setStats} />
            )}

            <div className="mt-8 flex items-center justify-end gap-3 pt-6 border-t border-gold/20">
              <button
                onClick={() => saveSection(tab)}
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors disabled:opacity-60"
                data-testid={`galeri-save-${tab}`}
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Simpan Perubahan
              </button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}

/* ---------- Sub editors ---------- */

function TentangEditor({ data, onChange }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif-display text-2xl text-forest">Tentang Pondok</h2>
        <p className="text-xs text-moss mt-1">2 gambar + deskripsi singkat yang tampil pada seksi Tentang.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SlotUploader
          label="Gambar 1 (utama)"
          value={data.image1}
          fallback={FALLBACK.tentang.image1}
          maxDim={1200}
          onChange={(v) => onChange({ ...data, image1: v })}
          testId="tentang-image1"
        />
        <SlotUploader
          label="Gambar 2 (pendukung)"
          value={data.image2}
          fallback={FALLBACK.tentang.image2}
          maxDim={1000}
          onChange={(v) => onChange({ ...data, image2: v })}
          testId="tentang-image2"
        />
      </div>
      <div>
        <label className="block text-xs tracking-[0.2em] uppercase text-moss mb-2">Deskripsi Singkat</label>
        <textarea
          value={data.description || ""}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          rows={3}
          maxLength={500}
          className="w-full rounded-xl border border-gold/30 bg-parchment px-4 py-3 text-sm focus:outline-none focus:border-forest"
          placeholder="Kalimat pengantar untuk seksi Tentang Pondok (opsional). Kosongkan untuk pakai default."
          data-testid="tentang-description"
        />
      </div>
    </div>
  );
}

function FasilitasEditor({ items, onChange }) {
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif-display text-2xl text-forest">Fasilitas</h2>
        <p className="text-xs text-moss mt-1">5 slot fasilitas — masing-masing dengan nama, deskripsi & gambar.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((it, i) => (
          <div key={i} className="p-4 rounded-xl border border-gold/25 bg-parchment/50">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">Slot {i + 1}</div>
            <SlotUploader
              label="Gambar Fasilitas"
              value={it.image}
              fallback={FALLBACK.fasilitas[i]?.image}
              maxDim={1400}
              onChange={(v) => update(i, { image: v })}
              testId={`fasilitas-image-${i}`}
            />
            <div className="mt-3 grid grid-cols-1 gap-3">
              <input
                type="text"
                value={it.name || ""}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder={FALLBACK.fasilitas[i]?.name || "Nama fasilitas"}
                maxLength={80}
                className="w-full rounded-lg border border-gold/30 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest"
                data-testid={`fasilitas-name-${i}`}
              />
              <textarea
                value={it.desc || ""}
                onChange={(e) => update(i, { desc: e.target.value })}
                rows={2}
                maxLength={200}
                placeholder="Deskripsi singkat (opsional)"
                className="w-full rounded-lg border border-gold/30 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest"
                data-testid={`fasilitas-desc-${i}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PengajarEditor({ items, onChange }) {
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif-display text-2xl text-forest">Pengajar</h2>
        <p className="text-xs text-moss mt-1">
          4 slot pengajar. Judul (Jabatan) & Subjudul (Nama) akan tampil di bawah foto.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((it, i) => (
          <div key={i} className="p-4 rounded-xl border border-gold/25 bg-parchment/50">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">Slot {i + 1}</div>
            <SlotUploader
              label="Foto Pengajar"
              value={it.image}
              fallback={FALLBACK.pengajar[i]?.image}
              aspect="portrait"
              maxDim={900}
              onChange={(v) => update(i, { image: v })}
              testId={`pengajar-image-${i}`}
            />
            <div className="mt-3 space-y-2">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Jabatan (judul)</label>
                <input
                  type="text"
                  value={it.jabatan || ""}
                  onChange={(e) => update(i, { jabatan: e.target.value })}
                  maxLength={60}
                  placeholder={FALLBACK.pengajar[i]?.jabatan || "Jabatan"}
                  className="mt-1 w-full rounded-lg border border-gold/30 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest"
                  data-testid={`pengajar-jabatan-${i}`}
                />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-moss">Nama (subjudul)</label>
                <input
                  type="text"
                  value={it.nama || ""}
                  onChange={(e) => update(i, { nama: e.target.value })}
                  maxLength={80}
                  placeholder={FALLBACK.pengajar[i]?.nama || "Nama pengajar"}
                  className="mt-1 w-full rounded-lg border border-gold/30 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest"
                  data-testid={`pengajar-nama-${i}`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GaleriEditor({ items, onChange }) {
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif-display text-2xl text-forest">Galeri</h2>
        <p className="text-xs text-moss mt-1">8 slot gambar galeri dengan caption pendek.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((it, i) => (
          <div key={i} className="p-4 rounded-xl border border-gold/25 bg-parchment/50">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">Slot {i + 1}</div>
            <SlotUploader
              label="Foto Galeri"
              value={it.image}
              fallback={FALLBACK.galeri[i]?.image}
              maxDim={1200}
              onChange={(v) => update(i, { image: v })}
              testId={`galeri-image-${i}`}
            />
            <input
              type="text"
              value={it.caption || ""}
              onChange={(e) => update(i, { caption: e.target.value })}
              maxLength={120}
              placeholder="Caption (opsional)"
              className="mt-3 w-full rounded-lg border border-gold/30 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest"
              data-testid={`galeri-caption-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function PendaftaranEditor({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-serif-display text-2xl text-forest">Pendaftaran</h2>
        <p className="text-xs text-moss mt-1">1 gambar/poster yang tampil di sisi kiri seksi Pendaftaran.</p>
      </div>
      <div className="max-w-lg">
        <SlotUploader
          label="Poster / Gambar Pendaftaran"
          value={data.image}
          fallback={FALLBACK.pendaftaran.image}
          maxDim={1400}
          onChange={(v) => onChange({ ...data, image: v })}
          testId="pendaftaran-image"
        />
      </div>
    </div>
  );
}

function IklanEditor({ items, onChange }) {
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif-display text-2xl text-forest">Iklan Berjalan</h2>
        <p className="text-xs text-moss mt-1">
          Maksimum 5 gambar iklan/sorotan pondok. Tambahkan hyperlink pada tiap gambar — audiens akan diarahkan ke link tersebut saat mengklik gambar. Iklan tampil sebagai banner berjalan di antara statistik dan seksi Tentang.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((it, i) => (
          <div key={i} className="p-4 rounded-xl border border-gold/25 bg-parchment/50">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">Slot {i + 1}</div>
            <SlotUploader
              label="Gambar Iklan"
              value={it.image}
              fallback=""
              maxDim={1400}
              onChange={(v) => update(i, { image: v })}
              testId={`iklan-image-${i}`}
            />
            <div className="mt-3">
              <label className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-moss mb-1">
                <LinkIcon size={12} /> Hyperlink (opsional)
              </label>
              <input
                type="url"
                value={it.link || ""}
                onChange={(e) => update(i, { link: e.target.value })}
                maxLength={500}
                placeholder="https://…"
                className="w-full rounded-lg border border-gold/30 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest"
                data-testid={`iklan-link-${i}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatistikEditor({ stats, onChange }) {
  const fields = [
    { key: "total_santri_aktif", label: "Santri Aktif", helper: "Jumlah santri yang aktif belajar di pondok" },
    { key: "total_hafidz_alumni", label: "Hafizh & Alumni", helper: "Total hafizh/hafizhah alumni pondok" },
    { key: "total_pengajar", label: "Ustadz & Ustadzah", helper: "Total tenaga pengajar" },
    { key: "tahun_berdiri", label: "Tahun Berdiri", helper: "Tahun berdirinya pondok" },
  ];
  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="mt-1 p-2 rounded-lg bg-forest text-parchment"><BarChart3 size={18} /></div>
        <div>
          <h2 className="font-serif-display text-2xl text-forest">Statistik Landing Page</h2>
          <p className="text-xs text-moss mt-1">
            Perbarui angka yang tampil pada banner statistik di landing page (Santri Aktif, Hafizh &amp; Alumni, Ustadz &amp; Ustadzah, Tahun Berdiri).
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.key} className="p-4 rounded-xl border border-gold/25 bg-parchment/50">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-gold mb-2">{f.label}</label>
            <input
              type="number"
              value={stats[f.key] ?? 0}
              min={0}
              max={f.key === "tahun_berdiri" ? 2200 : 100000}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === "" ? 0 : parseInt(raw, 10);
                onChange({ ...stats, [f.key]: Number.isNaN(n) ? 0 : n });
              }}
              className="w-full rounded-lg border border-gold/30 bg-white px-3 py-2 text-lg font-serif-display focus:outline-none focus:border-forest"
              data-testid={`stats-${f.key}`}
            />
            <p className="mt-1 text-[10px] text-moss">{f.helper}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Uploader (per slot) ---------- */
function SlotUploader({ label, value, fallback, onChange, testId, maxDim = 1000, aspect = "landscape" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const preview = value || fallback || "";

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Format harus JPG/PNG/WEBP");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Maksimal 8MB");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await resizeImage(file, maxDim);
      onChange(dataUrl);
    } catch {
      toast.error("Gagal memproses gambar");
    } finally { setBusy(false); }
  };

  return (
    <div>
      {label && <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-2">{label}</label>}
      <div
        className={`relative w-full ${aspect === "portrait" ? "aspect-[3/4]" : "aspect-[4/3]"} rounded-xl overflow-hidden border-2 border-dashed border-gold/40 bg-sand`}
      >
        {preview ? (
          <img src={preview} alt={label} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-moss">
            <ImageIcon size={24} />
            <span className="text-xs mt-2">Belum ada gambar</span>
          </div>
        )}
        {value && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-forest/85 text-parchment text-[10px] tracking-wider uppercase">
            Kustom
          </span>
        )}
        {!value && preview && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-white/85 text-forest text-[10px] tracking-wider uppercase">
            Default
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={testId}
      />
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-forest text-parchment text-xs hover:bg-gold transition-colors disabled:opacity-60"
          data-testid={`${testId}-pick`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {value ? "Ganti" : "Unggah"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs hover:bg-red-50"
            data-testid={`${testId}-clear`}
          >
            <X size={12} /> Reset
          </button>
        )}
      </div>
      <p className="mt-1 text-[10px] text-moss">Otomatis dikompres · Foto lama ditimpa saat diganti</p>
    </div>
  );
}
